import { RootStore } from './root';
import { HttpClient, HttpError, HttpResponse } from '@wix/http-client';
import type { EavsEmailValidationResult } from '../utils/eavsSignal';
import {
  GetSsoSettingsResponse,
  UsersAccountsDataByEmailRequest,
} from '@wix/ambassador-wix-html-login-webapp/http';
import { getUserAccountsByEmail } from '@wix/ambassador-users-v1-example/http';
import {
  loginV2,
  registerV2,
  verify,
  getWebauthnChallenge,
  loginWithIdpConnectionTokenParams,
  validateEmail as validateEmailRequest,
} from '@wix/ambassador-iam-authentication-v1-authentication/http';
import { serializeAuthenticationCredential } from '@wix/webauthn-management';

import {
  type ApiResponseData,
  type ExecuteStepUpAuthRequest,
  type InitiateStepUpAuthRequest,
  type AuthRequest,
  type UserAccountsDataByEmailResponse,
  type VerifyCodeAndLoginRequest,
  type VerifyRecoveryCodeRequest,
  type VerifyRecoveryCodeResponse,
  type SignupAuthRequest,
  type AuthAdditionalLoginData,
  type AuthAdditionalRegisterData,
} from '../types';
import {
  deviceVerifyV2,
  invalidateUserCode,
} from '@wix/ambassador-identity-oauth-v1-refresh-token/http';
import {
  DeviceCodeResponse,
  DeviceVerifyResponse,
  DeviceVerifyRequest,
  InvalidateUserCodeRequest,
  InvalidateUserCodeResponse,
} from '@wix/ambassador-identity-oauth-v1-refresh-token/types';
import { ERROR_CODES, extractServerErrorCode } from '../utils/errorHandler';
import { getChallenge } from '@wix/ambassador-identity-v1-verification/http';
import {
  GetChallengeResponse,
  ApprovalChallengeStatus,
} from '@wix/ambassador-identity-v1-verification/types';
import {
  API_PATH,
  CAPTCHA_KEY,
  DEVICE_AUTHORIZE_PATH,
  DEVICE_AUTH_GRANT_TYPE,
  DEVICE_AUTH_IDP_CONNECTION_ID,
  DEVICE_AUTH_ID_TOKEN_PARAM,
  EXPERIMENTS,
  OAUTH_TOKEN_PATH,
  SEARCH_PARAMS,
  TwoFactorAuthMethod,
  TwoFactorAuthMethods,
  TwoFactorAuthPhoneDeliveryMethods,
  USERS_API_PATH,
  factorsMapIamToLegacy,
  legacyFactorTypeToIamFactorType,
} from '../utils/constants';
import {
  sendAccountRecoveryCode,
  verifyAccountRecoveryCode,
} from '@wix/ambassador-identity-users-v1-user/http';
import {
  VerifyAccountRecoveryCodeRequest,
  VerifyAccountRecoveryCodeResponse,
} from '@wix/ambassador-identity-users-v1-user/types';
import { isAnswersUrl } from '../utils/general';
import {
  FactorType,
  DeliveryMethod,
  StateType,
  StateMachineResponse,
  GetWebauthnChallengeResponse,
  GetWebauthnChallengeRequest,
  LoginV2Request,
} from '@wix/ambassador-iam-authentication-v1-authentication/types';

/**
 * Bounded timeout (ms) for the pre-login user-accounts lookup. Without an
 * explicit value the request inherits axios's default of `0` (wait forever),
 * which can hang the login flow — and sled tests — indefinitely when the
 * endpoint stalls or is unreachable.
 */
const USER_ACCOUNTS_REQUEST_TIMEOUT_MS = 10_000;

/** Response of the OAuth /token endpoint for the device_code grant (RFC 8628). */
export interface DeviceTokenResponse {
  access_token?: string;
  token_type?: string;
  expires_in?: number;
  id_token?: string;
  /** OAuth error code while polling, e.g. `authorization_pending`, `expired_token`. */
  error?: string;
  error_description?: string;
}

export class ApiStore {
  private readonly rootStore: RootStore;
  private readonly httpClient: HttpClient;
  private readonly AUTH_SERVER_PATH: string;
  private readonly EAVS_REQUEST_TIMEOUT_MS = 5_000;
  private iamDisabled: boolean = false;
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.httpClient = new HttpClient();
    this.extendObjectFromQuery = this.extendObjectFromQuery.bind(this);
    this.AUTH_SERVER_PATH = this.rootStore.experiments.enabled(
      'specs.ident.useOriginInApiCalls',
    )
      ? location.origin + '/auth/'
      : '/auth/';
  }

  private transformRequest(obj: any) {
    return new URLSearchParams(obj).toString();
  }

  private logHttpResponseToRecorder(response: HttpResponse) {
    const { data, headers, config } = response;
    const { email, password, userName, ...payloadToCollect } =
      data?.payload ?? {};
    const requestId = response.requestId ?? headers['x-wix-request-id'];
    this.rootStore.addAttributeToWixRecorder(
      config.url ?? requestId,
      JSON.stringify({
        data: { ...data, payload: payloadToCollect },
        requestId,
      }),
    );
  }

  private responseProxy(request: Promise<HttpResponse>) {
    return request
      .then((response) => {
        this.logHttpResponseToRecorder(response);
        return response;
      })
      .catch((error: HttpError) => {
        error.response && this.logHttpResponseToRecorder(error.response);
        throw error;
      });
  }

  public login(data: AuthRequest) {
    const shouldUseIAMPlatform =
      !!data.stateToken ||
      this.shouldUseIAMPlatform(
        EXPERIMENTS.USE_IAM_FOR_USERS_LOGIN_CALLS,
        data,
      );
    return this.conditionallyUseIAMWithFallback(
      shouldUseIAMPlatform,
      () => this.loginWithIAM(data),
      () => this.loginWithLegacy(data),
    );
  }

  public getWebauthnChallenge(
    getWebauthnChallengeRequest: GetWebauthnChallengeRequest,
  ): Promise<GetWebauthnChallengeResponse> {
    return this.responseProxy(
      this.httpClient.request(
        getWebauthnChallenge(getWebauthnChallengeRequest),
      ),
    ).then((response) => response.data);
  }

  public validateEmail(email: string): Promise<EavsEmailValidationResult> {
    return this.responseProxy(
      this.httpClient.request((context) => ({
        ...validateEmailRequest({ email })(context),
        timeout: this.EAVS_REQUEST_TIMEOUT_MS,
      })),
    ).then((response) => response.data);
  }

  private loginWithIAM(data: AuthRequest) {
    const { userType } = this.rootStore;
    const loginV2Request = this.buildLoginV2Request(data, userType);

    return this.responseProxy(this.httpClient.request(loginV2(loginV2Request)))
      .then((response) => {
        if (response.data?.state === StateType.MFA_CHALLENGE) {
          throw response.data;
        }
        return {
          success: true,
          data: response.data,
        };
      })
      .catch((error) => {
        if (error.state === StateType.MFA_CHALLENGE) {
          throw {
            errorCode: ERROR_CODES.SECOND_FACTOR_REQUIRED,
            payload: error,
          };
        }
        const errorCode =
          error.response?.data?.details?.applicationError?.data?.rule ??
          error.response?.data?.details?.applicationError?.code;
        const errorData = error.response?.data?.details?.applicationError?.data;
        const statusCode = error.response?.status;

        // eslint-disable-next-line no-throw-literal
        throw {
          success: false,
          errorCode,
          errorData,
          statusCode,
          message: `Login failed: errorCode=${errorCode}, statusCode=${statusCode}, errorData=${JSON.stringify(
            errorData,
          )}`,
        };
      });
  }

  private buildLoginV2Request(
    authRequest: Partial<AuthRequest>,
    userType: string,
  ): LoginV2Request {
    const isWebAuthnFlow = !!authRequest.webAuthnAuthenticationData;

    if (isWebAuthnFlow) {
      return this.buildWebAuthnLoginRequest(authRequest, userType);
    }

    return this.buildPasswordLoginRequest(authRequest, userType);
  }

  private buildWebAuthnLoginRequest(
    authRequest: Partial<AuthRequest>,
    userType: string,
  ): LoginV2Request {
    const webAuthnData = authRequest.webAuthnAuthenticationData!;

    return {
      loginId: authRequest.email ? { email: authRequest.email } : {},
      password: null,
      captchaTokens: this.getCaptchas(authRequest as AuthRequest),
      clientMetaData: this.extendAuthParams(
        {
          color: authRequest.color,
          userType,
          refferalInfo: authRequest.referralInfo,
        },
        true,
      ),
      webauthn: {
        challengeId: webAuthnData.webAuthnChallenge.challengeId,
        credential: webAuthnData.publicKeyCredential
          ? serializeAuthenticationCredential(
              webAuthnData.publicKeyCredential as PublicKeyCredential,
            )
          : undefined,
      },
      stateToken: authRequest.stateToken,
    };
  }

  private buildPasswordLoginRequest(
    authRequest: Partial<AuthRequest>,
    userType: string,
  ): LoginV2Request {
    return {
      loginId: authRequest.email ? { email: authRequest.email } : {},
      password: authRequest.password ?? '',
      captchaTokens: this.getCaptchas(authRequest as AuthRequest),
      clientMetaData: this.extendAuthParams(
        {
          color: authRequest.color,
          userType,
          refferalInfo: authRequest.referralInfo,
        },
        true,
      ),
      webauthn: undefined,
      stateToken: authRequest.stateToken,
    };
  }

  public loginWithLegacy(data: AuthRequest) {
    return this.responseProxy(
      this.httpClient.post(
        `${this.AUTH_SERVER_PATH}v2/login`,
        this.extendAuthParams(data, false),
        {
          transformRequest: this.transformRequest,
          baseURL: `/${this.rootStore.userType}`,
        },
      ),
    ).then((response) => {
      if (response.data.success) {
        return response.data;
      }
      throw response.data;
    });
  }

  public signup(data: SignupAuthRequest) {
    this.addInviteTokenIfNeeded(data);
    const shouldUseIAMPlatform = this.shouldUseIAMPlatform(
      EXPERIMENTS.USE_IAM_FOR_USERS_CALLS,
      data,
    );
    const finalData = this.extendAuthParams(data, shouldUseIAMPlatform);

    return this.conditionallyUseIAMWithFallback(
      shouldUseIAMPlatform,
      () => this.signupWithIAM(finalData),
      () => this.signUpWithLegacy(finalData),
    );
  }

  private signupWithIAM(data: ExtendedSignupAuthRequest) {
    const { email, password, color, referralInfo } = data;
    const { userType } = this.rootStore;

    return this.responseProxy(
      this.httpClient.request(
        registerV2({
          loginId: {
            email,
          },
          password,
          captchaTokens: this.getCaptchas(data),
          profile: {
            customFields: [
              ...(data.color
                ? [
                    {
                      name: 'color',
                      value: {
                        strValue: data.color,
                      },
                    },
                  ]
                : []),
              {
                name: 'userType',
                value: {
                  strValue: this.rootStore.userType,
                },
              },
              {
                name: 'language',
                value: {
                  strValue: this.rootStore.language.locale,
                },
              },
            ],
          },
          clientMetaData: this.extendAuthParams(
            {
              color,
              userType,
              referralInfo,
            },
            true,
          ),
        }),
      ),
    )
      .then((response) => {
        return {
          success: true,
          data: response.data,
        };
      })
      .catch((error) => {
        const errorCode =
          error.response?.data?.details?.applicationError?.data?.rule ??
          error.response?.data?.details?.applicationError?.code;
        const statusCode = error.response?.status;

        // eslint-disable-next-line no-throw-literal
        throw {
          success: false,
          errorCode,
          statusCode,
        };
      });
  }

  private signUpWithLegacy(data: ExtendedSignupAuthRequest) {
    return this.responseProxy(
      this.httpClient.post(`${this.AUTH_SERVER_PATH}register`, data, {
        transformRequest: this.transformRequest,
        baseURL: `/${this.rootStore.userType}`,
      }),
    ).then((response) => {
      if (response.data.success) {
        return response.data;
      }
      throw response.data;
    });
  }

  // The SMS factor supports two delivery methods: SMS and WhatsApp.
  // - WhatsApp: return WhatsApp so the server sends the code via WhatsApp.
  // - SMS: return undefined, since a plain SMS is the backend default and
  //   needs no explicit delivery method.
  private smsDeliveryMethod(
    phoneDeliveryMethod?: string,
  ): DeliveryMethod | undefined {
    return phoneDeliveryMethod === TwoFactorAuthPhoneDeliveryMethods.WHATSAPP
      ? DeliveryMethod.WHATSAPP
      : undefined;
  }

  private shouldUseIAMPlatformVerify(factor: FactorType) {
    const IAMSupportedMethods = [
      FactorType.TOTP,
      FactorType.EMAIL,
      FactorType.SMS,
      FactorType.WEBAUTHN,
      this.rootStore.experiments.enabled(
        EXPERIMENTS.USE_IAM_PLATFORM_FOR_VOICE_CALL,
      )
        ? FactorType.CALL
        : undefined,
      this.rootStore.experiments.enabled(EXPERIMENTS.USE_IAM_FOR_PUSH_FACTOR)
        ? FactorType.PUSH
        : undefined,
    ];
    const isIAMSupportsFactorMethod =
      !!factor && IAMSupportedMethods.includes(factor);
    return isIAMSupportsFactorMethod;
  }
  public verifyCodeAndLogin(data: VerifyCodeAndLoginRequest) {
    const factor = legacyFactorTypeToIamFactorType(
      data.twoFAMethod,
      data.phoneDeliveryMethod,
    );
    const shouldUseIAMPlatform = this.shouldUseIAMPlatformVerify(factor);
    const { stateToken, phoneDeliveryMethod, ...dataWithoutStateToken } = data;
    const isOtpEmpty = false;
    return this.conditionallyUseIAMWithFallbackVerify(
      typeof data.stateToken !== 'undefined' && shouldUseIAMPlatform,
      isOtpEmpty,
      () => this.verifyCodeAndLoginWithIAM(data),
      () => this.verifyCodeAndLoginWithLegacy(dataWithoutStateToken),
    );
  }

  public verifyCodeAndLoginWithLegacy(data: VerifyCodeAndLoginRequest) {
    return this.responseProxy(
      this.httpClient.post(`${this.AUTH_SERVER_PATH}verifycodeandlogin`, data, {
        transformRequest: this.transformRequest,
      }),
    ).then((response) => response.data);
  }

  public verifyCodeAndLoginWithIAM(data: VerifyCodeAndLoginRequest) {
    const factor = legacyFactorTypeToIamFactorType(
      data.twoFAMethod,
      data.phoneDeliveryMethod,
    );

    const deliveryMethod = this.smsDeliveryMethod(data.phoneDeliveryMethod);
    // WebAuthn sends credential data; other factors send the code.
    // deliveryMethod is only added when defined: for WhatsApp it's added,
    // for SMS it's undefined so it isn't sent here.
    const factorData =
      factor === FactorType.WEBAUTHN && data.webauthnData
        ? data.webauthnData
        : { code: data.code, ...(deliveryMethod && { deliveryMethod }) };

    return this.responseProxy(
      this.httpClient.request(
        verify({
          [`${factor.toLowerCase()}Data`]: factorData,
          stateToken: data.stateToken,
          factorType: factor,
          rememberThisDevice: data.rememberMe,
        }),
      ),
    )
      .then((response) => {
        return {
          success: true,
          data: response.data,
        };
      })
      .catch((error) => {
        const errorCode =
          error.response?.data?.details?.applicationError?.data?.rule ??
          error.response?.data?.details?.applicationError?.code;
        const errorData = error.response?.data?.details?.applicationError?.data;
        const statusCode = error.response?.status;
        // Include stateToken from error response for subsequent calls
        const stateToken = error.response?.data?.stateToken;

        // eslint-disable-next-line no-throw-literal
        throw {
          success: false,
          errorCode,
          errorData,
          statusCode,
          stateToken,
        };
      });
  }

  public verifyRecoveryCode(
    data: VerifyRecoveryCodeRequest,
  ): Promise<VerifyRecoveryCodeResponse> {
    return this.responseProxy(
      this.httpClient.post(`${this.AUTH_SERVER_PATH}verifyrecoverycode`, data, {
        transformRequest: this.transformRequest,
      }),
    ).then((response) => response.data);
  }

  public verifyRecoveryCodeNewApi(
    data: VerifyAccountRecoveryCodeRequest,
  ): Promise<VerifyAccountRecoveryCodeResponse> {
    return this.responseProxy(
      this.httpClient.request(verifyAccountRecoveryCode(data)),
    ).then((response) => response.data);
  }

  public initiateStepUpAuth = (data: InitiateStepUpAuthRequest) => {
    return this.responseProxy(
      this.httpClient.post('/_api/v1/users/initiate-authentication', data),
    ).then((response) => response.data);
  };

  public initiateMockedStepUpAuth = (data: InitiateStepUpAuthRequest) => {
    return {
      verificationId: '123456789',
      methodType: TwoFactorAuthMethods.Phone,
      mfaHint: '******FAKE',
    };
  };

  public executeStepUpAuth = (data: ExecuteStepUpAuthRequest) => {
    return this.responseProxy(
      this.httpClient.post(
        `/_api/v1/users/execute-authentication/${data.verificationId ?? ''}`,
        data,
      ),
    ).then((response) => response.data);
  };

  public executeMockedStepUpAuth = (data: ExecuteStepUpAuthRequest) => {
    return this.responseProxy(
      this.httpClient.post(`/_api/wix-checking-fake-step-up-token`, {
        action: data.actionTag,
        state: data.state,
      }),
    ).then((response) => response.data);
  };

  public socialLogin<T extends AuthRequest>(
    data: T,
    provider: string,
  ): Promise<{
    data: ApiResponseData & { action?: string; studio?: boolean };
    headers: Function;
  }> {
    this.addInviteTokenIfNeeded(data);
    return this.responseProxy(
      this.httpClient.post(
        `/social/login/${provider}`,
        this.extendAuthParams(data, false),
        {
          transformRequest: this.transformRequest,
          baseURL: `/${this.rootStore.userType}`,
        },
      ),
    ).then((response) => response);
  }

  public resendRecoveryCode(email: string, parentAccountId?: string) {
    const shouldUseNewApi = this.rootStore.experiments.enabled(
      EXPERIMENTS.ACCOUNT_RECOVERY_NEW_API,
    );
    if (shouldUseNewApi) {
      return this.responseProxy(
        this.httpClient.request(
          sendAccountRecoveryCode({
            email,
            parentAccountId,
          }),
        ),
      ).then((response) => response.data);
    }
    return this.responseProxy(
      this.httpClient.post(
        `${this.AUTH_SERVER_PATH}sendrecoverycode?email=${email}${
          parentAccountId ? `&parentAccountId=${parentAccountId}` : ''
        }`,
        {},
        {
          transformRequest: this.transformRequest,
        },
      ),
    ).then((response) => response.data);
  }

  public resendSecondFactorCode(data: {
    twoFAToken: string;
    twoFAMethod: string;
    phoneDeliveryMethod?: string;
    stateToken?: string;
  }): Promise<
    ApiResponseData & {
      payload: {
        twoFAHint?: string;
        twoFAToken?: string;
        currentTwoFAMethod?: TwoFactorAuthMethod;
        verificationId?: string;
        enabledTwoFAMethods?: TwoFactorAuthMethod[];
        stateToken?: string;
        webauthnChallengeData?: {
          challengeId?: string;
          publicKeyCredentialRequestOptionsJson?: string;
        };
      };
    }
  > {
    const factor = legacyFactorTypeToIamFactorType(
      data.twoFAMethod,
      data.phoneDeliveryMethod,
    );
    const shouldUseIAMPlatform =
      this.shouldUseIAMPlatformVerify(factor) &&
      typeof data.stateToken !== 'undefined';
    const { stateToken, ...dataWithoutStateToken } = data;
    const dataIAM = {
      factor,
      stateToken: data.stateToken,
      phoneDeliveryMethod: data.phoneDeliveryMethod,
    };
    const isOtpEmpty = true;
    return this.conditionallyUseIAMWithFallbackVerify(
      shouldUseIAMPlatform,
      isOtpEmpty,
      () => this.resendSecondFactorCodeWithIAM(dataIAM),
      () => this.resendSecondFactorCodeWithLegacy(dataWithoutStateToken),
    );
  }

  public resendSecondFactorCodeWithLegacy(data) {
    return this.responseProxy(
      this.httpClient.post(`${this.AUTH_SERVER_PATH}resend2facode`, data, {
        transformRequest: this.transformRequest,
      }),
    ).then((res) => res.data);
  }

  public resendSecondFactorCodeWithIAM(data) {
    const deliveryMethod = this.smsDeliveryMethod(data.phoneDeliveryMethod);
    return this.responseProxy(
      this.httpClient.request(
        verify({
          [`${data.factor.toLowerCase()}Data`]: deliveryMethod
            ? { deliveryMethod }
            : {},
          stateToken: data.stateToken,
          factorType: data.factor,
        }),
      ),
    )
      .then((response) => {
        const twoFAHint =
          response?.data?.mfaChallengeData?.verificationChallengeData?.hint;
        const twoFAToken = this.rootStore.twoFactorAuthStore.twoFAToken;
        const verificationId =
          response?.data?.mfaChallengeData?.verificationChallengeData?.pushData
            ?.transactionId;
        const webauthnChallengeData =
          response?.data?.mfaChallengeData?.verificationChallengeData
            ?.webauthnData;
        const enabledTwoFAMethods =
          response?.data?.mfaChallengeData?.availableFactors.map(
            (item) => factorsMapIamToLegacy[item.factorType],
          );
        const stateToken = response?.data?.stateToken;
        return {
          success: true,
          payload: {
            ...response.data,
            twoFAHint,
            twoFAToken,
            verificationId,
            currentTwoFAMethod:
              this.rootStore?.twoFactorAuthStore?.currentTwoFAMethod,
            enabledTwoFAMethods,
            stateToken,
            webauthnChallengeData,
          },
        };
      })
      .catch((error) => {
        const errorCode =
          error.response?.data?.details?.applicationError?.data?.rule ??
          error.response?.data?.details?.applicationError?.code;
        const errorData = error.response?.data?.details?.applicationError?.data;
        const statusCode = error.response?.status;

        // eslint-disable-next-line no-throw-literal
        throw {
          success: false,
          errorCode,
          errorData,
          statusCode,
        };
      });
  }

  public resetPassword(data: {
    emailToken: string;
    newPassword: string;
  }): Promise<ApiResponseData & { payload: {} }> {
    return this.responseProxy(
      this.httpClient.post(`${USERS_API_PATH}/link/changePasswordAjax`, data, {
        transformRequest: this.transformRequest,
      }),
    ).then((res) => res.data);
  }

  public getUserDataByEmail(email: string): Promise<ApiResponseData> {
    return this.responseProxy(
      this.httpClient.get(`${API_PATH}userDataByEmail?email=${email}`),
    ).then((res) => res.data);
  }

  public async getUserSsoSettingsByEmail(
    email: string,
    parentAccountId?: string,
  ): Promise<GetSsoSettingsResponse> {
    return this.responseProxy(
      this.httpClient.get(
        `${API_PATH}ssoAuthUrl?email=${encodeURIComponent(email)}${
          parentAccountId
            ? `&parentAccountId=${encodeURIComponent(parentAccountId)}`
            : ''
        }`,
      ),
    ).then((res) => res.data);
  }

  public async getMissingRenderParams(): Promise<{
    basename: string;
    ssoRedirectUrl: string;
    ssoExistingWixAccountEmail: string;
    ssoAccountId: string;
    ssoAccountImage: string;
    ssoAccountName: string;
    currentLoggedInUserEmail: string;
  }> {
    return this.responseProxy(
      this.httpClient.get(`${API_PATH}missingRenderModel${location.search}`),
    ).then((res) => res.data);
  }

  public getUserAccounts(
    data: UsersAccountsDataByEmailRequest,
  ): Promise<UserAccountsDataByEmailResponse> {
    const accountsRequest = getUserAccountsByEmail(data);
    return this.responseProxy(
      this.httpClient.request((context) => ({
        ...accountsRequest(context),
        timeout: USER_ACCOUNTS_REQUEST_TIMEOUT_MS,
      })),
    ).then((res) => res.data as UserAccountsDataByEmailResponse);
  }

  public getFunnelRedirectUrl({
    precondition,
    postAuthUrl,
    redirectToUrl,
    authMethod,
    originUrl,
  }: {
    precondition: string;
    postAuthUrl: string;
    redirectToUrl: string;
    authMethod?: string;
    originUrl?: string;
  }): Promise<{ url: string }> {
    const preconditionParam = `precondition=${precondition}`;
    const postAuthUrlParam =
      postAuthUrl && postAuthUrl !== 'undefined'
        ? `&postAuthUrl=${postAuthUrl}`
        : '';
    const authMethodParam = authMethod ? `&authMethod=${authMethod}` : '';
    const redirectToUrlParam =
      redirectToUrl && redirectToUrl !== 'undefined'
        ? `&redirectToUrl=${redirectToUrl}`
        : '';
    const originUrlParam =
      originUrl && originUrl !== 'undefined' ? `&originUrl=${originUrl}` : '';
    const headers = this.rootStore.displayStore.getPresetHeaders();
    return this.httpClient
      .get(
        `/_api/funnel-intro-router-service/v2/intro?${preconditionParam}${postAuthUrlParam}${authMethodParam}${redirectToUrlParam}${originUrlParam}`,
        {
          headers,
          baseURL: '',
        },
      )
      .then((res: any) => res.data.introDestination);
  }

  public getCurrentPage(): Promise<{}> {
    return this.httpClient.get(window.location.href);
  }

  public deviceVerifyCode(
    code: Omit<DeviceVerifyRequest, 'userCode'>,
  ): Promise<DeviceVerifyResponse> {
    return this.httpClient
      .request(deviceVerifyV2({ userCode: code as string }))
      .then((res) => res.status);
  }

  /**
   * RFC 8628 step 1 — request a device + user code (the authorization transaction).
   * Direct same-origin JSON POST to /oauth/device/authorize. We can't go through
   * the oauth ambassador client here: its `deviceCode` maps to
   * /v1/oauth/device/code, which isn't routed on users.wix.com and 404s.
   * The endpoint expects a JSON body (a form-urlencoded body 400s with
   * "Could not parse JSON") and replies with a camelCase JSON body that already
   * matches `DeviceCodeResponse` (deviceCode/userCode/verificationUri/expiresIn;
   * `interval` is omitted, the store falls back to DEVICE_AUTH_DEFAULT_INTERVAL).
   */
  public requestDeviceCode(
    clientId: string,
    scope: string,
  ): Promise<DeviceCodeResponse> {
    return this.responseProxy(
      this.httpClient.post(
        DEVICE_AUTHORIZE_PATH,
        { client_id: clientId, scope },
        { baseURL: '' },
      ),
    ).then((response) => response.data as DeviceCodeResponse);
  }

  /**
   * RFC 8628 step 2 — poll the token endpoint with the device_code grant.
   * Direct form-urlencoded POST to the public /oauth/token endpoint. `transformRequest`
   * serializes the body to urlencoded and the http-client derives the matching
   * Content-Type from it — don't also set the header explicitly, or it gets
   * appended on top of the auto-set one and the server receives a doubled
   * `application/x-www-form-urlencoded, application/x-www-form-urlencoded` it can't
   * parse (400). While the device is not yet authorized the endpoint replies with an
   * OAuth error body (`authorization_pending`, `expired_token`, ...); we normalize
   * that so the caller always sees a `{ error }`-shaped rejection it can branch on.
   */
  public pollDeviceToken(
    clientId: string,
    deviceCodeValue: string,
  ): Promise<DeviceTokenResponse> {
    return this.httpClient
      .post(
        OAUTH_TOKEN_PATH,
        {
          grant_type: DEVICE_AUTH_GRANT_TYPE,
          client_id: clientId,
          device_code: deviceCodeValue,
        },
        {
          transformRequest: this.transformRequest,
          baseURL: '',
        },
      )
      .then((response) => response.data as DeviceTokenResponse)
      .catch((error: HttpError) => {
        const oauthError = (error.response?.data ?? {}) as DeviceTokenResponse;
        if (oauthError.error) {
          throw oauthError;
        }
        throw error;
      });
  }

  /**
   * Device flow step 3 — exchange the OIDC `id_token` minted by /oauth/token for
   * a real Wix session via IAM `loginWithIdpConnectionTokenParams`
   * (POST /v1/sso/login-with-token-params). The IDP connection validates the
   * token and the returned `StateMachineResponse` carries the session, so only a
   * successful response means the user is actually logged in. The id_token must
   * be sent under the `idToken` key (see DEVICE_AUTH_ID_TOKEN_PARAM).
   */
  public loginWithDeviceIdToken(
    idToken: string,
  ): Promise<StateMachineResponse> {
    return this.responseProxy(
      this.httpClient.request(
        loginWithIdpConnectionTokenParams({
          idpConnectionId: DEVICE_AUTH_IDP_CONNECTION_ID,
          tokenParams: { [DEVICE_AUTH_ID_TOKEN_PARAM]: idToken },
        }),
      ),
    ).then((response) => response.data as StateMachineResponse);
  }

  public getChallengeIam(
    verificationId: string,
    stateToken: string | undefined,
    rememberThisDevice?: boolean,
  ): Promise<{
    response: GetChallengeResponse;
    stateToken?: string | undefined;
  }> {
    let status: ApprovalChallengeStatus;
    return this.responseProxy(
      this.httpClient.request(
        verify({
          pushData: {
            transactionId: verificationId,
          },
          stateToken,
          factorType: FactorType.PUSH,
          rememberThisDevice,
        }),
      ),
    )
      .then((response) => {
        if (response?.data?.state === StateType.SUCCESS) {
          status = ApprovalChallengeStatus.APPROVED;
        } else if (response?.data?.state === StateType.MFA_CHALLENGE) {
          status = ApprovalChallengeStatus.PENDING_APPROVAL;
        }
        return {
          response: { challenge: { approvalChallenge: { status } } },
          stateToken: response?.data?.stateToken,
        };
      })
      .catch((error) => {
        const errorCode =
          error.response?.data?.details?.applicationError?.data?.rule ??
          error.response?.data?.details?.applicationError?.code;
        const errorData = error.response?.data?.details?.applicationError?.data;
        const statusCode = error.response?.status;
        if (
          errorCode === ERROR_CODES.VERIFICATION_CANCELED ||
          errorCode === ERROR_CODES.FACTOR_NOT_FOUND
        ) {
          return {
            response: {
              challenge: {
                approvalChallenge: { status: ApprovalChallengeStatus.DECLINED },
              },
            },
          };
        }
        if (
          errorCode === ERROR_CODES.OTP_EXPIRED ||
          errorCode === ERROR_CODES.EXPIRED_JWT_TOKEN
        ) {
          return {
            response: {
              challenge: {
                approvalChallenge: { status: ApprovalChallengeStatus.EXPIRED },
              },
            },
          };
        }
        // eslint-disable-next-line no-throw-literal
        throw {
          success: false,
          errorCode,
          errorData,
          statusCode,
        };
      });
  }

  public getChallengeLegacy(verificationId: string): Promise<{
    response: GetChallengeResponse;
    stateToken?: string | undefined;
  }> {
    return this.httpClient
      .request(getChallenge({ verificationId }))
      .then((res) => {
        return { response: res.data };
      });
  }

  public async getChallenge(
    verificationId: string,
    stateToken: string | undefined,
    rememberThisDevice?: boolean,
  ): Promise<{
    response: GetChallengeResponse;
    stateToken?: string | undefined;
  }> {
    const shouldUseIAMPlatform =
      this.shouldUseIAMPlatformVerify(FactorType.PUSH) &&
      stateToken !== undefined;
    if (shouldUseIAMPlatform) {
      return this.getChallengeIam(
        verificationId,
        stateToken,
        rememberThisDevice,
      );
    }
    return this.getChallengeLegacy(verificationId);
  }

  public deviceInvalidateUserCode(
    code: Omit<InvalidateUserCodeRequest, 'userCode'>,
  ): Promise<InvalidateUserCodeResponse> {
    return this.httpClient
      .request(invalidateUserCode({ userCode: code as string }))
      .then((res) => res.status);
  }

  private addInviteTokenIfNeeded(data: { [key: string]: any }) {
    const inviteToken =
      this.rootStore.navigationStore.getQueryParam('inviteToken');
    if (inviteToken) {
      data.inviteToken = inviteToken;
    }
  }

  public extendAuthParams<T extends {}>(params: T = {} as T, useIAM: boolean) {
    if (useIAM) {
      return {
        ...params,
        ldSessionId: this.rootStore.sessionId.get(),
        originView: this.rootStore.displayStore.isMobile ? 'mobile' : 'desktop',
        urlThatUserRedirectedFrom: this.rootStore.originUrl,
        language: this.rootStore.language.locale,
      };
    }

    return {
      ...params,
      ldSessionID: this.rootStore.sessionId.get(),
      originView: this.rootStore.displayStore.isMobile ? 'mobile' : 'desktop',
      urlThatUserRedirectedFrom: this.rootStore.originUrl,
      overrideLocale: this.rootStore.language.locale,
      language: this.rootStore.language.locale,
    };
  }

  public extendObjectFromQuery<T extends { [key: string]: any }>(
    currentObject: T,
    paramKey: string,
    precondition?: (value: string) => boolean,
  ): T & { [k: typeof paramKey]: string | undefined } {
    const param = this.rootStore.navigationStore.getQueryParam(paramKey);
    Object.assign(currentObject, {
      ...((precondition && !precondition(param)) || !param
        ? {}
        : { [paramKey]: param }),
    });

    return currentObject;
  }

  // TODO when merging this FT we should also modify the ldSessionID param name
  private shouldUseIAMPlatform(spec: string, params: Record<string, any>) {
    return (
      this.rootStore.experiments.enabled(spec) &&
      !params.userToken &&
      !params.inviteToken &&
      !params.studio &&
      document.location.origin === 'https://users.wix.com' &&
      !isAnswersUrl(
        new URLSearchParams(location.search).get(
          SEARCH_PARAMS.POST_LOGIN_URL,
        ) ?? '',
      )
    );
  }

  public async conditionallyUseIAMWithFallbackVerify<T>(
    shouldUseIAMPlatform: boolean,
    isOtpEmpty: boolean,
    iam: () => Promise<T>,
    fallback: () => Promise<T>,
  ): Promise<T> {
    if (!shouldUseIAMPlatform) {
      this.rootStore.twoFactorAuthStore.stateToken = undefined;
      return fallback();
    }
    try {
      return await iam();
    } catch (error) {
      const statusCode = (error as any)?.statusCode as number;
      if (
        extractServerErrorCode(error) === ERROR_CODES.UNIMPLEMENTED_FEATURE ||
        (isOtpEmpty && statusCode >= 500 && statusCode < 600)
      ) {
        this.rootStore.twoFactorAuthStore.stateToken = undefined;
        return fallback();
      }
      throw error;
    }
  }

  public async conditionallyUseIAMWithFallback<T>(
    shouldUseIAMPlatform: boolean,
    iam: () => Promise<T>,
    fallback: () => Promise<T>,
  ): Promise<T> {
    if (!shouldUseIAMPlatform || this.iamDisabled) {
      return fallback();
    }

    try {
      return await iam();
    } catch (error) {
      const statusCode = (error as any)?.statusCode as number;
      let shouldFallback = false;

      if (statusCode >= 500 && statusCode < 600) {
        this.iamDisabled = true;
        shouldFallback = true;
      }

      if (
        shouldFallback ||
        extractServerErrorCode(error) === ERROR_CODES.UNIMPLEMENTED_FEATURE
      ) {
        return fallback();
      }

      throw error;
    }
  }

  private getCaptchas(data: AuthRequest) {
    if (data[CAPTCHA_KEY]) {
      return [{ Recaptcha: data[CAPTCHA_KEY] }];
    }
    return [{ InvisibleRecaptcha: data.invisibleRecaptcha }];
  }

  public applyAdditionalLoginParams<T extends object>(
    params: T,
  ): T & AuthAdditionalLoginData {
    const paramsNames = [SEARCH_PARAMS.COLOR, SEARCH_PARAMS.REFERRAL_INFO];
    paramsNames.forEach((key) => this.extendObjectFromQuery(params, key));
    return params;
  }

  public applyAdditionalSignupParams<T extends {}>(
    params: T,
  ): T & AuthAdditionalRegisterData {
    const { extendObjectFromQuery } = this;
    const boolParamsNames = [SEARCH_PARAMS.SEND_EMAIL, SEARCH_PARAMS.IS_STUDIO];
    const paramsNames = [
      SEARCH_PARAMS.COLOR,
      SEARCH_PARAMS.USER_TOKEN,
      SEARCH_PARAMS.REFERRAL_INFO,
      SEARCH_PARAMS.INVITE_TOKEN,
    ];
    boolParamsNames.forEach((key) =>
      extendObjectFromQuery(params, key, booleanParamPrecondition),
    );
    paramsNames.forEach((key) => extendObjectFromQuery(params, key));
    return params;
  }

  public buildIdpCustomPayloadExtras(): Record<string, string> {
    const extras = this.applyAdditionalSignupParams(
      this.extendAuthParams({}, false),
    ) as unknown as Record<string, unknown>;
    // userType is in the URL path of /social/login (baseURL: `/${userType}`),
    // not the body. Forward it explicitly so the IDP server has the same context.
    extras.userType = this.rootStore.userType;
    const result: Record<string, string> = {};
    Object.entries(extras).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        result[key] = String(value);
      }
    });
    return result;
  }
}
const booleanParamPrecondition = (val) => val === 'true' || val === 'false';

type ExtendedSignupAuthRequest = ReturnType<
  typeof ApiStore.prototype.extendAuthParams<SignupAuthRequest>
>;
